/**
 * Debug overlay showing live joint angle measurements and corrective shape key weights.
 *
 * Split into two parts:
 * - JointAngleDataPump: R3F component that measures angles each frame and writes
 *   to a global for the DOM-side panel to read. Only active when debug mode is on.
 * - JointAngleDebugPanel: DOM component using DraggablePanel for a draggable,
 *   persistent debug window.
 */

import { useFrame } from "@react-three/fiber";
import { useCallback, useEffect, useRef } from "react";
import type * as THREE from "three/webgpu";
import { getLastAppliedPresetName } from "../app/defaultPoses.ts";
import { useStore } from "../app/store.ts";
import {
  type CorrectiveBinding,
  computeCorrectiveWeights,
  computeTheoreticalMaxWeight,
} from "../mannequin/correctiveShapeKeys.ts";
import {
  CORRECTIVE_JOINT_TARGETS,
  measureJointAngles,
} from "../mannequin/jointAngles.ts";
import { CopyButton } from "../ui/CopyButton.tsx";
import { DraggablePanel } from "../ui/DraggablePanel.tsx";
import { usePanelSelection } from "../ui/floatingPanelStore.ts";
import { Z_DEBUG } from "../ui/tokens.ts";

// ---------------------------------------------------------------------------
// Shared global for R3F → DOM data bridge
// ---------------------------------------------------------------------------

export interface JointAngleRow {
  label: string;
  boneName: string;
  axis: string;
  angle: number;
  maxWeight: number;
  /** Whether an actual corrective binding exists for this bone/axis */
  hasBinding: boolean;
}

export interface JointAngleSnapshot {
  rows: JointAngleRow[];
  bindingCount: number;
}

// ---------------------------------------------------------------------------
// Bar rendering
// ---------------------------------------------------------------------------

const BAR_LENGTH = 10;

function renderBar(weight: number): string {
  const filled = Math.round(weight * BAR_LENGTH);
  return "\u2588".repeat(filled) + "\u2591".repeat(BAR_LENGTH - filled);
}

// ---------------------------------------------------------------------------
// R3F data pump — lives inside the Canvas, only active when debug is on
// ---------------------------------------------------------------------------

export function JointAngleDataPump({
  boneMap,
  restPoses,
  bonePoses,
  correctiveBindings,
}: {
  boneMap: Map<string, THREE.Bone>;
  restPoses: Map<string, [number, number, number, number]>;
  bonePoses: Map<string, [number, number, number, number]> | undefined;
  correctiveBindings: readonly CorrectiveBinding[];
}) {
  const bindingsRef = useRef(correctiveBindings);
  bindingsRef.current = correctiveBindings;

  useFrame(() => {
    // Measure per-axis angles for the debug display (still useful for artists)
    const angles = measureJointAngles(
      CORRECTIVE_JOINT_TARGETS,
      boneMap,
      restPoses,
      bonePoses,
    );
    // Compute corrective weights using PSD (quaternion distance)
    const weights = computeCorrectiveWeights(
      bindingsRef.current,
      boneMap,
      restPoses,
      bonePoses,
    );

    // Pre-compute per-row max weight so the DOM side doesn't need binding metadata.
    // When actual bindings exist, use the real computed weights. Otherwise fall back
    // to the theoretical weight derived from JOINT_ACTION_MAP definitions.
    const rows: JointAngleRow[] = angles.map((a) => {
      let maxWeight = 0;
      let hasBinding = false;

      if (bindingsRef.current.length > 0) {
        for (const b of bindingsRef.current) {
          if (b.boneName === a.boneName) {
            hasBinding = true;
            const w = weights.get(b.morphIndex) ?? 0;
            if (w > maxWeight) maxWeight = w;
          }
        }
      }

      // If no binding matched (or bindings are empty), compute the theoretical
      // weight from the bone's delta quaternion.
      if (maxWeight === 0) {
        const rest = restPoses.get(a.boneName);
        const bone = boneMap.get(a.boneName);
        if (rest && bone) {
          const stored = bonePoses?.get(a.boneName);
          const cx = stored ? stored[0] : bone.quaternion.x;
          const cy = stored ? stored[1] : bone.quaternion.y;
          const cz = stored ? stored[2] : bone.quaternion.z;
          const cw = stored ? stored[3] : bone.quaternion.w;
          // delta = inv(rest) * current
          const rx = -rest[0],
            ry = -rest[1],
            rz = -rest[2],
            rw = rest[3];
          const dx = rw * cx + rx * cw + ry * cz - rz * cy;
          const dy = rw * cy - rx * cz + ry * cw + rz * cx;
          const dz = rw * cz + rx * cy - ry * cx + rz * cw;
          const dw = rw * cw - rx * cx - ry * cy - rz * cz;
          maxWeight = computeTheoreticalMaxWeight(a.boneName, dx, dy, dz, dw);
        }
      }

      return {
        label: a.label,
        boneName: a.boneName,
        axis: a.axis,
        angle: a.angle,
        maxWeight,
        hasBinding,
      };
    });

    window.__poseref_jointAngles = {
      rows,
      bindingCount: bindingsRef.current.length,
    };
  });

  return null;
}

// ---------------------------------------------------------------------------
// DOM-side draggable panel
// ---------------------------------------------------------------------------

export function JointAngleDebugPanel() {
  const debugEnabled = useStore((s) => s.debugEnabled);
  const panelOpen = useStore((s) => s.debugPanelOpen.jointAngles);
  const poseMode = useStore((s) => s.poseMode);
  const togglePanel = useStore((s) => s.toggleDebugPanel);
  const sel = usePanelSelection("debug-joint-angles", () =>
    togglePanel("jointAngles"),
  );
  const contentRef = useRef<HTMLDivElement>(null);
  const presetRef = useRef<HTMLSpanElement>(null);

  // Poll the global at animation frame rate for smooth updates
  useEffect(() => {
    if (!debugEnabled || !panelOpen || !poseMode) return;

    let raf: number;
    const tick = () => {
      raf = requestAnimationFrame(tick);
      const snap = window.__poseref_jointAngles;
      if (!snap || !contentRef.current) return;

      // Update preset name display
      if (presetRef.current) {
        const name = getLastAppliedPresetName();
        presetRef.current.textContent = name ? `  Preset: ${name}` : "";
      }

      const el = contentRef.current;
      const rows = el.children;

      for (let i = 0; i < snap.rows.length && i < rows.length; i++) {
        const r = snap.rows[i];
        const deg = Math.abs((r.angle * 180) / Math.PI);

        const row = rows[i] as HTMLElement;
        const pct = Math.round(r.maxWeight * 100);
        row.textContent =
          `${r.label.padEnd(9)} (${r.boneName}.${r.axis})`.padEnd(24) +
          `${(r.angle < 0 ? "-" : " ") + deg.toFixed(1).padStart(5)}°  ${renderBar(r.maxWeight)}  ${String(pct).padStart(3)}%`;
        // Red text only when the model HAS some corrective bindings but this
        // specific bone/axis is missing one (gap in corrective coverage).
        // When model has no correctives at all, everything stays green.
        const disconnected = snap.bindingCount > 0 && !r.hasBinding;
        row.style.color = disconnected ? "#f44" : "#0f0";
      }
    };

    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [debugEnabled, panelOpen, poseMode]);

  const getPlainText = useCallback(() => {
    const snap = window.__poseref_jointAngles;
    if (!snap) return "";
    const presetName = getLastAppliedPresetName();
    const lines: string[] = [
      "JOINT ANGLES" + (presetName ? `  Preset: ${presetName}` : ""),
    ];
    for (const r of snap.rows) {
      const deg = Math.abs((r.angle * 180) / Math.PI);
      const pct = Math.round(r.maxWeight * 100);
      lines.push(
        `${r.label.padEnd(9)} (${r.boneName}.${r.axis})`.padEnd(24) +
          `${(r.angle < 0 ? "-" : " ") + deg.toFixed(1).padStart(5)}°  ${renderBar(r.maxWeight)}  ${String(pct).padStart(3)}%`,
      );
    }
    return lines.join("\n");
  }, []);

  if (!debugEnabled || !panelOpen || !poseMode) return null;

  return (
    <DraggablePanel
      id="debug-joint-angles"
      defaultPosition={{ bottom: 8, left: 8 }}
      zIndex={Z_DEBUG}
      collapsible
      selectionColor="#ff0"
      {...sel}
      style={{ minWidth: 300 }}
      header={
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            width: "100%",
            fontFamily: "monospace",
            fontSize: 10,
          }}
        >
          <span style={{ color: "#ff0", fontWeight: "bold" }}>
            JOINT ANGLES
            <span ref={presetRef} style={{ color: "#0ff" }} />
          </span>
          <CopyButton getText={getPlainText} />
        </div>
      }
    >
      <div
        style={{
          background: "rgba(0,0,0,0.85)",
          color: "#0f0",
          fontFamily: "monospace",
          fontSize: 10,
          padding: "6px 10px",
          borderRadius: "0 0 6px 6px",
          pointerEvents: "auto",
          lineHeight: 1.6,
        }}
      >
        <div ref={contentRef} style={{ color: "#0f0" }}>
          {CORRECTIVE_JOINT_TARGETS.map((t) => (
            <div key={`${t.boneName}:${t.axis}`}>
              {`${t.label.padEnd(9)} (${t.boneName}.${t.axis})`.padEnd(24) +
                "  0.0°  " +
                renderBar(0) +
                "   0%"}
            </div>
          ))}
        </div>
      </div>
    </DraggablePanel>
  );
}
